import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { setNewTaskVariables } from "../../../actions/task";
import { useWorkflowContext } from "../../../components/Workflow/WorkflowContextProvider";
import { useEditorContext } from "../../editor.Context";
import { customVariablesClasses, generateUniqueId, smartChipClasses } from "../../editor.helper";
import { useTaskVariableUpdates, useWorkflowVariableUpdates } from "./hooks/taskCustomVariable.hooks";
import { buildTaskCustomVariablePayloads, findExistingTaskCustomVariable, resolveTaskVariableDetails } from "./taskCustomVariable.utils";

/**
 * used to render dropdown type component in task description variable
 * @author {Prachi Jain}
 */
const CustomVariableDropdown = ({ updateNonEditable, variableDetails, smartChipId, setShowEditChip, editorRef }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const parentRef = useRef();
  const dropdownRef = useRef();
  const { user } = useSelector((state) => state.auth);
  const { isNewTask, task, workflowCustomVariables } = useSelector((state) => state.taskSidebar);
  const { taskCustomVariables, setTaskCustomVariables, taskCustomVariablesRef } = useEditorContext();
  const { addTaskCustomVariables, addWorklowDescriptionCustomVariables } = useTaskVariableUpdates();
  const { updateSmartWorkflowVariables } = useWorkflowVariableUpdates();
  const dispatch = useDispatch();
  const workflowState = useWorkflowContext();
  const location = useLocation();

  const exisitingVariable = useMemo(
    () =>
      findExistingTaskCustomVariable({
        smartChipId,
        taskCustomVariables,
        workflowContextId: workflowState?.workflow?.Id,
      }),
    [smartChipId, taskCustomVariables, workflowState?.workflow?.Id]
  );

  const newVariableDetails = useMemo(
    () => resolveTaskVariableDetails(variableDetails, exisitingVariable, workflowCustomVariables),
    [variableDetails, exisitingVariable, workflowCustomVariables]
  );

  useEffect(() => {
    if (smartChipId) return setSelectedValue(exisitingVariable?.variableValue ?? { label: `Select ${newVariableDetails?.name}`, value: null });
    setSelectedValue(
      newVariableDetails?.attributes?.default ? newVariableDetails?.attributes?.default : { label: `Select ${newVariableDetails?.name}`, value: null }
    );
  }, [smartChipId, newVariableDetails?.attributes]);

  /**
   * Updates React state, ref, and smart-workflow mirror for task custom variables (CustomVariableDropdown).
   * @author Abhishek Dhillon
   * @param {Array} nextVariables - Full next list of task / workflow description variables
   */
  const syncTaskCustomVariablesState = (nextVariables) => {
    setTaskCustomVariables(nextVariables);
    taskCustomVariablesRef.current = [...nextVariables];
    updateSmartWorkflowVariables(nextVariables);
  };

  const updateExistingSmartChip = (chosenValue, chipClass) => {
    const { label, value } = chosenValue;
    const updateChip = editorRef?.current?.element?.querySelector(`#${smartChipId}`);
    updateChip.innerHTML = label;
    setShowEditChip(false);
    const taskCustomVariable = { ...exisitingVariable, variableValue: chosenValue };
    /** works only when workflow page is opened but task sidebar is not open */
    const newTaskCustomVariables = taskCustomVariables.map((item) => (item.taskVariableId === smartChipId ? { ...taskCustomVariable } : item));
    syncTaskCustomVariablesState(newTaskCustomVariables);
    if (!isNewTask) addTaskCustomVariables([...newTaskCustomVariables]);
    else dispatch(setNewTaskVariables([...newTaskCustomVariables]));
  };

  const addNewSmartChip = async (chosenValue, chipClass) => {
    const { label, value } = chosenValue ?? { label: `Select ${newVariableDetails?.name}`, value: null };
    const id = await generateUniqueId(user.id, "customDropdown", value);
    updateNonEditable(workflowState?.workflow?.Id ? newVariableDetails?.name : label, id, chipClass);
    const { newTaskCustomVariable, newWorkflowCustomVariable } = buildTaskCustomVariablePayloads({
      variableDetails,
      userId: user.id,
      variableValue: chosenValue,
      generatedId: id,
    });
    /** works only when workflow page is opened but task sidebar is not open */
    if (location?.pathname?.includes("/workflow") && !isNewTask && !task?.taskId) {
      const updatedWorkflowCustomVariables = [...taskCustomVariables, newWorkflowCustomVariable];
      syncTaskCustomVariablesState(updatedWorkflowCustomVariables);
      if (workflowState?.workflow?.Id) addWorklowDescriptionCustomVariables(updatedWorkflowCustomVariables, workflowState?.workflow);
      else workflowState?.setworkflowDescriptionVariables(updatedWorkflowCustomVariables);
    } else {
      const updatedTaskCustomVariables = [...taskCustomVariables, newTaskCustomVariable];
      syncTaskCustomVariablesState(updatedTaskCustomVariables); // remove smart chips
      if (!isNewTask) addTaskCustomVariables(updatedTaskCustomVariables);
      else dispatch(setNewTaskVariables(updatedTaskCustomVariables));
    }
  };

  /**
   * Handles smart chip selection logic for both existing and new chips
   * @param {Object} chosenValue - The selected value from dropdown
   * @author Sarthak Arora
   */
  const handleSmartChipSelection = (chosenValue) => {
    const chipClass = newVariableDetails?.chipType === "smart chip" ? smartChipClasses[0] : customVariablesClasses[0];
    if (smartChipId && !workflowState?.workflow?.Id) {
      updateExistingSmartChip(chosenValue, chipClass);
    } else if (!smartChipId) {
      addNewSmartChip(chosenValue, chipClass);
    }
  };

  const updateNode = async (e) => {
    const chosenValue = e.target.value;
    setSelectedValue(chosenValue);
    const isClickEvent = e.nativeEvent.type === "click";
    if (isClickEvent) {
      handleSmartChipSelection(chosenValue);
    }
  };

  /**
   * Handles Enter key press to trigger selection
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isOpen) {
      e.preventDefault();
      if (selectedValue) {
        handleSmartChipSelection(selectedValue);
      }
    }
  };

  /**
   * Handles dropdown open/close state
   */
  const handleOpenChange = (e) => {
    setIsOpen(true);
    setFilteredData(getDdList());
  };

  /**
   * Generates the dropdown list options
   * @returns {Array} Array of options including:
   * - Default "Select" option with null value
   * - Additional options from variableDetails attributes
   * @author {Abhishek}
   */
  const getDdList = () => {
    if (newVariableDetails) {
      return [
        { label: `Select ${newVariableDetails?.name}`, value: null }, // default option
        ...(newVariableDetails?.attributes?.options || []), // additional options from variable details
      ];
    } else {
      return [];
    }
  };

  const [filteredData, setFilteredData] = useState(getDdList());

  /**
   * Handles the filter change event from the dropdown
   * Filters the dropdown options based on the search text
   * @param {Object} e - Filter event object containing the search text
   * @param {string} e.filter.value - The current search text entered by user
   */
  const handleFilterChange = (e) => {
    const filterValue = e.filter.value.toLowerCase();
    const filtered = getDdList().filter((item) => item.label.toLowerCase().includes(filterValue));
    setFilteredData(filtered);
  };

  return (
    <div>
      {!!(workflowState?.workflow?.Id && !smartChipId) && handleSmartChipSelection(newVariableDetails?.attributes?.default)}
      {!workflowState?.workflow?.Id && (
        <div style={{ minWidth: "200px" }} className='dt-editor-menu'>
          <div className='w-100' id={`update-editor-custom-dropdown-chip`} ref={parentRef}>
            <div className='d-flex justify-content-between p-2 pb-0'>
              <div className='font-weight-bold'>{newVariableDetails?.name}</div>
            </div>
            <div onKeyDownCapture={handleKeyDown} key={newVariableDetails?.name} className='p-2'>
              <DropDownList
                ref={dropdownRef}
                filterable={true}
                onFilterChange={handleFilterChange}
                onOpen={handleOpenChange}
                onClose={() => setFilteredData([])}
                popupSettings={{ appendTo: parentRef.current }}
                value={selectedValue}
                onChange={(e) => updateNode(e)}
                data={filteredData}
                textField='label'
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomVariableDropdown;
